'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Menu, Phone, Shield, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import type { Header } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import { resolveLinkHref } from '@/utilities/resolveLinkHref'

interface HeaderClientProps {
  data: Header
}

export const HeaderClient: React.FC<HeaderClientProps> = ({ data }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const pathname = usePathname()

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const closeMenu = () => setIsMenuOpen(false)

  const navItems = data?.navItems || []
  const cta = data?.ctaLink
  const ctaHref = resolveLinkHref(cta)

  return (
    <nav className="fixed top-4 left-0 right-0 z-50 transition-all duration-300 px-4 flex justify-center">
      <div
        className={`w-full max-w-6xl border border-white/40 shadow-xl rounded-2xl flex justify-between items-center px-6 py-3 transition-all duration-300 ${
          scrolled ? 'bg-white/95' : 'bg-white/90'
        }`}
      >
        <Link href="/" onClick={closeMenu} className="flex items-center gap-3 cursor-pointer">
          <div className="bg-gradient-to-br from-red-600 to-red-800 text-white p-2 rounded-xl shadow-lg shadow-red-600/20">
            <Shield size={20} />
          </div>
          <div className="leading-tight">
            <h1 className="text-lg font-bold text-slate-900 tracking-tight">{data?.companyName}</h1>
            <p className="text-[10px] text-red-600 font-bold uppercase tracking-widest">
              {data?.role}
            </p>
          </div>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-8 font-medium text-sm text-slate-600">
          {navItems.map(({ link }, i) => {
            const href = resolveLinkHref(link)
            const isActive = Boolean(href && href !== '/' && pathname?.startsWith(href))
            return (
              <CMSLink
                key={i}
                {...link}
                appearance="inline"
                className={`relative transition-colors after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:bg-red-600 after:transition-all after:duration-300 ${
                  isActive
                    ? 'text-slate-900 after:w-full'
                    : 'hover:text-red-600 after:w-0 hover:after:w-full'
                }`}
              />
            )
          })}
          {ctaHref && (
            <Link
              href={ctaHref}
              className={cn(buttonVariants({ variant: 'dark' }), 'rounded-xl px-5 py-2.5')}
            >
              <Phone size={16} /> {cta?.label}
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden p-2 rounded-lg hover:bg-slate-100/50 transition-colors"
          aria-label="Menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="absolute top-full mt-2 w-[92%] max-w-md bg-white/98 rounded-2xl border border-white/50 shadow-2xl p-6 flex flex-col gap-4 animate-in fade-in slide-in-from-top-4">
          {navItems.map(({ link }, i) => {
            const href = resolveLinkHref(link)
            if (!href) return null
            return (
              <Link
                key={i}
                href={href}
                onClick={closeMenu}
                className="text-lg font-medium p-2 hover:bg-slate-50 rounded-lg text-left"
              >
                {link?.label}
              </Link>
            )
          })}
          {ctaHref && (
            <Link
              href={ctaHref}
              onClick={closeMenu}
              className="text-red-600 font-bold p-2 bg-red-50 rounded-lg text-center"
            >
              {cta?.label}
            </Link>
          )}
        </div>
      )}
    </nav>
  )
}
